import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { marked } from "marked";
import { Download, Loader2, AlertCircle, ExternalLink, Sparkles } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Vista pública del output de una tool_run compartida (sin auth).
 * Reutiliza los mismos códigos visuales que `ConsultingSharedReport`:
 * franja lateral verde, badge dorado, `.consulting-md` para tipografía.
 */
export default function ConsultingSharedRun() {
  const { token } = useParams();
  const [state, setState] = useState({ loading: true, error: null, data: null });

  useEffect(() => {
    let mounted = true;
    axios
      .get(`${API_BASE}/consulting/public/tool_runs/${token}`)
      .then((r) => mounted && setState({ loading: false, error: null, data: r.data }))
      .catch((e) => mounted && setState({ loading: false, error: e?.response?.data?.detail || "Enlace no válido", data: null }));
    return () => { mounted = false; };
  }, [token]);

  if (state.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <Loader2 className="w-8 h-8 animate-spin text-[#0F6E56]" />
      </div>
    );
  }

  if (state.error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50 px-6">
        <div className="max-w-md text-center">
          <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h1 className="font-display text-2xl font-bold text-slate-900 mb-2">Enlace no disponible</h1>
          <p className="text-slate-600 mb-6">{state.error}</p>
          <a href="/" className="inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-sm font-semibold text-white" style={{ background: "#0F6E56" }}>
            Ir al inicio <ExternalLink className="w-4 h-4" />
          </a>
        </div>
      </div>
    );
  }

  const { tool_title, title, company_name, output_md, generated_at } = state.data;
  const pdfUrl = `${API_BASE}/consulting/public/tool_runs/${token}/pdf`;
  const dateStr = generated_at
    ? new Date(generated_at).toLocaleDateString("es-ES", { day: "2-digit", month: "long", year: "numeric" })
    : "";

  return (
    <div className="min-h-screen py-8 md:py-12 px-4 md:px-6" style={{ background: "#fafafa" }} data-testid="consulting-shared-run">
      <div className="max-w-4xl mx-auto">
        {/* Cabecera */}
        <div className="mb-8">
          <div className="text-[10px] font-bold uppercase tracking-widest mb-3" style={{ color: "#0F6E56" }}>
            Recurso operativo · Consultoría OptimizaRRHH
          </div>
          <div className="text-sm font-semibold text-slate-500 mb-1">{tool_title || "Generación"}</div>
          <h1 className="font-display text-3xl md:text-4xl font-bold text-slate-900 leading-tight">{title}</h1>
          {company_name && (
            <p className="text-sm text-slate-500 mt-2">Para {company_name}{dateStr ? ` · ${dateStr}` : ""}</p>
          )}
        </div>

        {/* Barra premium con CTA de PDF */}
        <div
          className="mb-8 rounded-2xl px-5 py-4 md:py-5 flex flex-col sm:flex-row sm:items-center gap-4 relative overflow-hidden"
          style={{
            background: "linear-gradient(135deg, #0b3d2e 0%, #14523f 55%, #0F6E56 100%)",
            boxShadow: "0 20px 45px -25px rgba(11,61,46,0.55)",
          }}
        >
          <span aria-hidden className="absolute bottom-0 left-0 right-0 h-1" style={{ background: "linear-gradient(90deg, #C9A84C 0%, #E5C874 50%, #C9A84C 100%)" }} />
          <div className="flex-1 min-w-0">
            <div className="text-[10px] font-bold uppercase tracking-widest" style={{ color: "#E5C874" }}>
              Vista compartida · Solo lectura
            </div>
            <div className="text-sm text-white/90 mt-1">
              Documento elaborado con la metodología de OptimizaRRHH a partir del diagnóstico del equipo. Descargable en PDF.
            </div>
          </div>
          <a
            href={pdfUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-2.5 text-sm font-bold whitespace-nowrap flex-shrink-0 w-full sm:w-auto transition-transform hover:-translate-y-0.5"
            style={{
              background: "linear-gradient(135deg, #C9A84C 0%, #E5C874 100%)",
              color: "#0b3d2e",
              boxShadow: "0 8px 22px -8px rgba(201,168,76,0.55)",
            }}
            data-testid="shared-run-pdf-btn"
          >
            <Download className="w-4 h-4" strokeWidth={2.5} /> Descargar PDF
          </a>
        </div>

        {/* Contenido en una sección con el mismo lenguaje visual */}
        <section
          className="rounded-2xl bg-white border border-slate-200 p-6 md:p-8 relative overflow-hidden"
          style={{ boxShadow: "0 4px 16px -8px rgba(11,61,46,0.08)" }}
          data-testid="shared-run-content"
        >
          <span
            aria-hidden
            className="absolute left-0 top-0 bottom-0 w-1"
            style={{ background: "linear-gradient(180deg, #0b3d2e 0%, #0F6E56 100%)" }}
          />
          <div className="mb-5 flex items-center gap-3 pl-2">
            <div
              className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
              style={{ background: "linear-gradient(135deg, #0b3d2e 0%, #0F6E56 100%)" }}
            >
              <Sparkles className="w-4 h-4 text-white" strokeWidth={2.25} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-widest text-slate-400">
                Pieza operativa · {tool_title || ""}
              </div>
              <div className="text-sm font-bold text-slate-900 font-display">{title}</div>
            </div>
          </div>
          <div
            className="prose prose-slate max-w-none consulting-md pl-2"
            dangerouslySetInnerHTML={{ __html: marked.parse(output_md || "", { async: false }) }}
          />
        </section>

        <p className="text-center text-xs text-slate-400 mt-10">
          Elaborado con la metodología OptimizaRRHH · <a href="/" className="hover:underline text-[#0F6E56]">talenthealthcheck.com</a>
        </p>
      </div>
    </div>
  );
}
